import { createApi } from '@reduxjs/toolkit/dist/query/react'
import baseQueryWithOnlineCourse from '../../app/baseQueryWithOnlineCourse'

export interface SalaryFactor {
    key: string
    inputPercent: number
    amount: number
    maxAmount: number
    source: string
    manualOverride: boolean
    doneCount?: number
    totalCount?: number
}

export interface StudentSalaryRow {
    userId: number
    userCourseEnrollId: number
    userSubjectEnrollId: number
    firstName: string
    lastName: string
    groupTitle: string
    factors: SalaryFactor[]
    total: number
    maxTotal: number
}

export interface SalaryCycle {
    cycleNumber: number
    startDate: string
    endDate: string
    status: string
    closedAt: string
    courseSubjectId: number | null
}

export interface SalaryTrendEntry {
    cycleNumber: number
    total: number
}

export interface MentorSalaryRow {
    mentorUserId: number
    firstName: string
    lastName: string
    courseSubjectId: number
    subjectTitleKz: string
    subjectTitleRu: string
    studentCount: number
    total: number
    maxTotal: number
    surveyFilled: boolean
    closed: boolean
    groupId: number | null
    groupTitle: string
    approvalStatus: SalaryApprovalStatus
    approvalComment: string
    // v2 (сервис басшысы): средние % за месяц/неделю, опрос (-1 = не введён), берілді
    avgVideo?: number | null
    avgTest?: number | null
    avgPractice?: number | null
    avgZachet?: number | null
    surveyPercent?: number | null
    paid?: boolean
    paidAt?: string | null
    approvedAt?: string | null
    baseAmount?: number | null
    courseId?: number | null
    subjectId?: number | null
    courseTitleKz?: string
    courseTitleRu?: string
    headNames?: string[]
}

// бекіту пән басшы по ментору за месяц
export type SalaryApprovalStatus = 'PENDING' | 'APPROVED' | 'RETURNED'

export interface MentorSalaryList {
    mentors: MentorSalaryRow[]
    mentorCount: number
    totalPayable: number
    averageSalary: number
    filledCount: number
    cycle: SalaryCycle
    groups: { id: number, title: string }[]
}

export interface MentorSalaryDetail {
    mentorUserId: number
    firstName: string
    lastName: string
    courseSubjectId: number
    subjectTitleKz: string
    subjectTitleRu: string
    cycleNumber: number
    students: StudentSalaryRow[]
    total: number
    maxTotal: number
    studentCount: number
    surveyPercent: number
    trend: SalaryTrendEntry[]
    cycle: SalaryCycle
    groupId: number
    weekNumber: number
    potoks: {
        courseSubjectId: number
        titleKz: string
        titleRu: string
        courseId: number | null
        subjectTitleKz: string
        subjectTitleRu: string
        groups: { groupId: number; title: string }[]
    }[]
    groups: { groupId: number; title: string }[]
    approvalStatus: SalaryApprovalStatus
    approvalComment: string
    approvalDecidedBy: string
    approvalDecidedAt: string
    paid?: boolean
    paidAt?: string | null
    // даты периода из календаря сервис басшысы и окно выдачи
    periodStart?: string | null
    periodEnd?: string | null
    payFrom?: string | null
    payTo?: string | null
}

export interface SalaryConfigEntry {
    key: string
    value: number
}

export interface SalaryAuditEntry {
    id: number
    cycleNumber: number
    mentorUserId: number
    actor: string
    action: string
    detail: string
    createdAt: string
}

export const salaryApiSlice = createApi({
    reducerPath: 'salaryApi',
    baseQuery: baseQueryWithOnlineCourse,
    tagTypes: ['SalaryList', 'SalaryDetail', 'MySalary', 'Config', 'Cycle', 'Audit'],
    endpoints: (build) => ({
        getMentorSalaryList: build.query<
            MentorSalaryList,
            { cycleNumber?: number; courseSubjectId?: number; groupId?: number; filter?: string }
        >({
            query: (params) => ({ url: 'mentor-salary/list', params }),
            providesTags: [{ type: 'SalaryList', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        getMentorSalaryDetail: build.query<
            MentorSalaryDetail,
            { mentorUserId: number; courseSubjectId?: number; cycleNumber?: number; weekNumber?: number }
        >({
            query: (params) => ({ url: 'mentor-salary/detail', params }),
            providesTags: (result, error, arg) => [
                { type: 'SalaryDetail', id: `${arg.mentorUserId}:${arg.courseSubjectId ?? 0}` },
                { type: 'SalaryDetail', id: 'LIST' },
            ],
            transformResponse: (response: any) => response.data,
        }),

        getMySalary: build.query<
            MentorSalaryDetail,
            { courseSubjectId?: number; cycleNumber?: number; groupId?: number; weekNumber?: number }
        >({
            query: (params) => ({ url: 'mentor-salary/my', params }),
            providesTags: [{ type: 'MySalary', id: 'ME' }],
            transformResponse: (response: any) => response.data,
        }),

        saveSurvey: build.mutation<
            any,
            { mentorUserId: number; courseSubjectId: number; cycleNumber: number; percent: number }
        >({
            query: (body) => ({ url: 'mentor-salary/survey', method: 'POST', body }),
            invalidatesTags: [
                { type: 'SalaryList', id: 'LIST' },
                { type: 'SalaryDetail', id: 'LIST' },
                { type: 'Audit', id: 'LIST' },
            ],
            transformResponse: (response: any) => response.data,
        }),

        saveManualFactor: build.mutation<
            any,
            { userSubjectEnrollId: number; cycleNumber: number; factor: string; percent: number }
        >({
            query: (body) => ({ url: 'mentor-salary/manual-factor', method: 'POST', body }),
            invalidatesTags: [
                { type: 'SalaryList', id: 'LIST' },
                { type: 'SalaryDetail', id: 'LIST' },
                { type: 'Audit', id: 'LIST' },
            ],
            transformResponse: (response: any) => response.data,
        }),

        getSalaryConfig: build.query<{ entries: SalaryConfigEntry[] }, void>({
            query: () => ({ url: 'mentor-salary/config' }),
            providesTags: [{ type: 'Config', id: 'ALL' }],
            transformResponse: (response: any) => response.data,
        }),

        updateSalaryConfig: build.mutation<any, { entries: SalaryConfigEntry[] }>({
            query: (body) => ({ url: 'mentor-salary/config', method: 'PUT', body }),
            invalidatesTags: [
                { type: 'Config', id: 'ALL' },
                { type: 'SalaryList', id: 'LIST' },
                { type: 'SalaryDetail', id: 'LIST' },
            ],
            transformResponse: (response: any) => response.data,
        }),

        getSalaryCycles: build.query<SalaryCycle[], void>({
            query: () => ({ url: 'mentor-salary/cycles' }),
            providesTags: [{ type: 'Cycle', id: 'ALL' }],
            transformResponse: (response: any) => response.data,
        }),

        closeCycle: build.mutation<any, { cycleNumber: number, courseSubjectId: number }>({
            query: ({ cycleNumber, courseSubjectId }) => ({
                url: 'mentor-salary/cycle/close',
                method: 'POST',
                params: { cycleNumber, courseSubjectId },
            }),
            invalidatesTags: [
                { type: 'Cycle', id: 'ALL' },
                { type: 'SalaryList', id: 'LIST' },
                { type: 'SalaryDetail', id: 'LIST' },
            ],
            transformResponse: (response: any) => response.data,
        }),

        // Пән басшы: бекіту или қайтару (комментарий обязателен)
        decideSalary: build.mutation<
            any,
            { mentorUserId: number; courseSubjectId: number; cycleNumber: number; decision: 'approve' | 'return'; comment?: string }
        >({
            query: ({ mentorUserId, decision, ...body }) => ({
                url: `subject-head/salary/${mentorUserId}/${decision}`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [
                { type: 'SalaryList', id: 'LIST' },
                { type: 'SalaryDetail', id: 'LIST' },
                { type: 'Audit', id: 'LIST' },
            ],
        }),

        getSalaryAudit: build.query<
            { entries: SalaryAuditEntry[] },
            { cycleNumber?: number; mentorUserId?: number }
        >({
            query: (params) => ({ url: 'mentor-salary/audit', params }),
            providesTags: [{ type: 'Audit', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),
    }),
})
